import { useMemo } from 'react'
import { CheckCircle2, Clock, AlertTriangle, Users, Wrench } from 'lucide-react'
import { FaseDetail, StatoCommessa } from '../../types/commesse'

interface GanttChartProps {
  fasi: FaseDetail[]
  dataInizio: string | null
  dataConsegna: string
  stato: StatoCommessa
}

export function GanttChart({ fasi, dataInizio, dataConsegna, stato }: GanttChartProps) {
  const isRelative = !dataInizio || stato === 'pianificazione'

  const { bars, totalDays, todayOffset, consegnaOffset, dayWidth } = useMemo(() => {
    if (fasi.length === 0) {
      return { bars: [], totalDays: 0, todayOffset: -1, consegnaOffset: -1, dayWidth: 0 }
    }

    if (isRelative) {
      // Modalita' relativa: usa offset_inizio/offset_fine
      const maxOffset = Math.max(...fasi.map(f => f.offset_fine), 7)
      const totalDays = maxOffset + 3

      const dayWidth = Math.max(30, Math.min(60, 900 / totalDays))

      const bars = fasi.map(fase => ({
        fase,
        startDay: fase.offset_inizio,
        plannedDays: fase.durata_prevista,
        actualDays: Math.max(fase.durata_prevista, fase.in_ritardo ? fase.giorni_effettivi : fase.durata_prevista),
      }))

      return { bars, totalDays, todayOffset: -1, consegnaOffset: -1, dayWidth }
    } else {
      // Modalita' calendario: date reali
      const projectStart = new Date(dataInizio)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const consegna = new Date(dataConsegna)

      let maxEnd = consegna
      fasi.forEach(f => {
        if (f.data_fine_prevista) {
          const fineEffettiva = new Date(f.data_fine_prevista)
          if (f.in_ritardo && f.giorni.length > 0) {
            const lastGiorno = new Date(f.giorni[f.giorni.length - 1].data)
            if (lastGiorno > fineEffettiva) {
              fineEffettiva.setTime(lastGiorno.getTime() + 86400000)
            }
          }
          if (fineEffettiva > maxEnd) maxEnd = fineEffettiva
        }
      })

      const endDate = new Date(maxEnd)
      endDate.setDate(endDate.getDate() + 3)

      const totalDays = Math.max(
        Math.ceil((endDate.getTime() - projectStart.getTime()) / 86400000),
        7
      )

      const dayWidth = Math.max(30, Math.min(60, 900 / totalDays))
      const todayOffset = Math.max(0, (today.getTime() - projectStart.getTime()) / 86400000)
      const consegnaOffset = (consegna.getTime() - projectStart.getTime()) / 86400000

      const bars = fasi.map(fase => {
        const startDay = fase.data_inizio_prevista
          ? (new Date(fase.data_inizio_prevista).getTime() - projectStart.getTime()) / 86400000
          : fase.offset_inizio
        const plannedDays = fase.durata_prevista
        const actualDays = Math.max(plannedDays, fase.in_ritardo ? fase.giorni_effettivi : plannedDays)

        return { fase, startDay, plannedDays, actualDays }
      })

      return { bars, totalDays, todayOffset, consegnaOffset, dayWidth }
    }
  }, [fasi, dataInizio, dataConsegna, stato, isRelative])

  if (fasi.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
        <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p>Nessuna fase definita. Aggiungi fasi per visualizzare il Gantt.</p>
      </div>
    )
  }

  // Genera etichette asse X
  const dayLabels: { day: number; label: string }[] = []
  const step = totalDays > 30 ? 7 : totalDays > 14 ? 3 : 1

  if (isRelative) {
    for (let i = 0; i <= totalDays; i += step) {
      dayLabels.push({ day: i, label: `G${i + 1}` })
    }
  } else {
    const projectStart = new Date(dataInizio!)
    for (let i = 0; i <= totalDays; i += step) {
      const d = new Date(projectStart)
      d.setDate(d.getDate() + i)
      dayLabels.push({
        day: i,
        label: d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
      })
    }
  }

  const getBarColor = (fase: FaseDetail) => {
    if (isRelative) return 'bg-gray-400'
    if (fase.in_ritardo) return 'bg-red-500'
    if (fase.completata) return 'bg-green-500'
    if (fase.giorni_effettivi > 0) return 'bg-blue-500'
    return 'bg-gray-300'
  }

  const getBarBgColor = (fase: FaseDetail) => {
    if (isRelative) return 'bg-gray-200'
    if (fase.in_ritardo) return 'bg-red-100'
    if (fase.completata) return 'bg-green-100'
    if (fase.giorni_effettivi > 0) return 'bg-blue-100'
    return 'bg-gray-100'
  }

  const getStatusIcon = (fase: FaseDetail) => {
    if (isRelative) return <Clock className="w-3.5 h-3.5 text-gray-400" />
    if (fase.in_ritardo) return <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
    if (fase.completata) return <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
    if (fase.giorni_effettivi > 0) return <Clock className="w-3.5 h-3.5 text-blue-500" />
    return <Clock className="w-3.5 h-3.5 text-gray-400" />
  }

  const chartWidth = totalDays * dayWidth

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-fit">
          {/* Header con date/giorni */}
          <div className="flex border-b border-gray-200">
            <div className="w-56 flex-shrink-0 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">
              Fase
            </div>
            <div className="relative" style={{ width: chartWidth }}>
              <div className="flex">
                {dayLabels.map((dl, i) => (
                  <div
                    key={i}
                    className="text-xs text-gray-400 py-2 text-center border-l border-gray-100"
                    style={{ position: 'absolute', left: dl.day * dayWidth, width: step * dayWidth }}
                  >
                    {dl.label}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Righe fasi */}
          {bars.map(({ fase, startDay, plannedDays, actualDays }) => {
            const numPartecipanti = Math.max(1, fase.partecipanti_ids?.length || 0)
            const oreFase = fase.durata_prevista * numPartecipanti

            return (
              <div key={fase.id} className="flex border-b border-gray-100 hover:bg-gray-50 transition-colors">
                {/* Label fase */}
                <div className="w-56 flex-shrink-0 px-4 py-3 flex items-center gap-2">
                  {getStatusIcon(fase)}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{fase.titolo}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>
                        {fase.durata_prevista}g x {numPartecipanti} = {oreFase}h
                      </span>
                      {fase.tipo === 'parallelo' && <span>| Par.</span>}
                    </div>
                    {(fase.partecipanti_ids?.length > 0 || fase.strumenti_ids?.length > 0) && (
                      <div className="flex items-center gap-2 mt-0.5">
                        {fase.partecipanti_ids?.length > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-blue-600">
                            <Users className="w-3 h-3" /> {fase.partecipanti_ids.length}
                          </span>
                        )}
                        {fase.strumenti_ids?.length > 0 && (
                          <span className="flex items-center gap-0.5 text-xs text-amber-600">
                            <Wrench className="w-3 h-3" /> {fase.strumenti_ids.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Barra */}
                <div className="relative py-2" style={{ width: chartWidth }}>
                  {/* Griglia verticale */}
                  {dayLabels.map((dl, i) => (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0 border-l border-gray-50"
                      style={{ left: dl.day * dayWidth }}
                    />
                  ))}

                  {/* Barra prevista (sfondo) */}
                  <div
                    className={`absolute top-3 h-6 rounded ${getBarBgColor(fase)}`}
                    style={{
                      left: startDay * dayWidth,
                      width: Math.max(plannedDays * dayWidth, dayWidth),
                    }}
                  />

                  {/* Barra effettiva (solo se non relative) */}
                  {!isRelative && (
                    <div
                      className={`absolute top-3 h-6 rounded ${getBarColor(fase)} opacity-80`}
                      style={{
                        left: startDay * dayWidth,
                        width: Math.max(
                          (fase.giorni_effettivi > 0 ? actualDays : 0) * dayWidth,
                          fase.giorni_effettivi > 0 ? dayWidth : 0
                        ),
                      }}
                      title={`${fase.titolo}: ${fase.giorni_effettivi}/${fase.durata_prevista} giorni`}
                    />
                  )}

                  {/* Barra eccedenza (rosso) */}
                  {!isRelative && fase.in_ritardo && (
                    <div
                      className="absolute top-3 h-6 rounded-r bg-red-500 opacity-40"
                      style={{
                        left: (startDay + plannedDays) * dayWidth,
                        width: (actualDays - plannedDays) * dayWidth,
                      }}
                    />
                  )}

                  {/* Etichetta giorni sulla barra */}
                  {plannedDays > 0 && (
                    <div
                      className="absolute top-3.5 h-5 flex items-center justify-center text-xs font-medium"
                      style={{
                        left: startDay * dayWidth + 4,
                        color: !isRelative && (fase.giorni_effettivi > 0 || fase.completata) ? 'white' : '#6B7280',
                      }}
                    >
                      {fase.durata_prevista}g
                    </div>
                  )}

                  {/* Linea OGGI (solo calendario) */}
                  {!isRelative && todayOffset >= 0 && todayOffset <= totalDays && (
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-dashed border-primary-500 z-10"
                      style={{ left: todayOffset * dayWidth }}
                    />
                  )}

                  {/* Linea Consegna (solo calendario) */}
                  {!isRelative && consegnaOffset >= 0 && consegnaOffset <= totalDays && (
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-amber-400 z-10"
                      style={{ left: consegnaOffset * dayWidth }}
                    />
                  )}
                </div>
              </div>
            )
          })}

          {/* Legenda */}
          <div className="flex items-center gap-6 px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            {isRelative ? (
              <>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-300" />
                  <span>Pianificata</span>
                </div>
                <span className="text-gray-400 italic">Avvia la commessa per visualizzare date e avanzamento</span>
              </>
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-gray-300" />
                  <span>Pianificata</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-blue-500" />
                  <span>In corso</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-green-500" />
                  <span>Completata</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-red-500" />
                  <span>In ritardo</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-0 h-3 border-l-2 border-dashed border-primary-500" />
                  <span>Oggi</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-0 h-3 border-l-2 border-amber-400" />
                  <span>Consegna</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
